import React from 'react';
import { graphql, Link } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';

const ProjectsHero = styled.section`
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${props => props.theme.space[20]} ${props => props.theme.space[6]};
  background: linear-gradient(
    135deg,
    var(--color-bg) 0%,
    var(--color-surface) 100%
  );
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 30% 50%,
      rgba(255, 107, 53, 0.15) 0%,
      transparent 60%
    );
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: ${props => props.theme.containers.lg};
  margin: 0 auto;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSizes['6xl']};
  margin-bottom: ${props => props.theme.space[4]};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.accent} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSizes['4xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSizes.xl};
  color: var(--color-text-secondary);
  max-width: 700px;
  margin: 0 auto;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
`;

const ProjectsSection = styled.section`
  max-width: ${props => props.theme.containers.xl};
  margin: 0 auto;
  padding: ${props => props.theme.space[16]} ${props => props.theme.space[6]};
`;

const CategorySection = styled.div`
  margin-bottom: ${props => props.theme.space[16]};
`;

const CategoryTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSizes['4xl']};
  margin-bottom: ${props => props.theme.space[8]};
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: ${props => props.theme.space[4]};

  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.primary} 0%,
      transparent 100%
    );
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.space[8]};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(Link)`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: ${props => props.theme.radii.xl};
  padding: 0;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: ${props => props.hasImage
    ? `url(${props.imageSrc}) center/cover no-repeat`
    : `linear-gradient(
      135deg,
      rgba(255, 107, 53, 0.1) 0%,
      rgba(0, 78, 137, 0.1) 100%
    )`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.typography.fontSizes['6xl']};
  color: ${props => props.theme.colors.primary};
  opacity: ${props => props.hasImage ? 1 : 0.3};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.hasImage
      ? 'linear-gradient(180deg, transparent 0%, rgba(var(--color-bg-rgb), 0.7) 100%)'
      : 'none'};
  }
`;

const ProjectInfo = styled.div`
  padding: ${props => props.theme.space[6]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.space[3]};
  flex-grow: 1;
`;

const ProjectCategory = styled.span`
  display: inline-block;
  padding: ${props => props.theme.space[1]} ${props => props.theme.space[3]};
  background: rgba(255, 107, 53, 0.1);
  border-radius: ${props => props.theme.radii.full};
  font-size: ${props => props.theme.typography.fontSizes.xs};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: ${props => props.theme.typography.letterSpacings.wider};
  align-self: flex-start;
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSizes['2xl']};
  color: var(--color-text);
  margin: 0;
`;

const ProjectDescription = styled.p`
  color: var(--color-text-secondary);
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  flex-grow: 1;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.space[20]};
  color: var(--color-text-secondary);

  h2 {
    font-size: ${props => props.theme.typography.fontSizes['3xl']};
    margin-bottom: ${props => props.theme.space[4]};
  }
`;

function ProjectsPage({ data }) {
  const { t } = useTranslation();
  const projects = data.allMarkdownRemark.nodes;

  // Group projects by category
  const workProjects = projects.filter(p => p.frontmatter.category === 'work');
  const funProjects = projects.filter(p => p.frontmatter.category === 'fun');

  const renderProjectCard = (project) => {
    const lang = project.frontmatter.lang || 'en-us';
    const basePath = lang === 'en-us' ? '/projects' : `/${lang}/proyectos`;
    const path = `${basePath}/${project.frontmatter.page_id}`;
    const hasImage = project.frontmatter.img && project.frontmatter.img.trim() !== '';

    return (
      <ProjectCard key={project.id} to={path}>
        <ProjectImage
          hasImage={hasImage}
          imageSrc={hasImage ? project.frontmatter.img : null}
        >
          {!hasImage && (project.frontmatter.importance || '•')}
        </ProjectImage>
        <ProjectInfo>
          {project.frontmatter.category && (
            <ProjectCategory>
              {t(`projects.category.${project.frontmatter.category}`, project.frontmatter.category)}
            </ProjectCategory>
          )}
          <ProjectTitle>{project.frontmatter.title}</ProjectTitle>
          {project.frontmatter.description && (
            <ProjectDescription>
              {project.frontmatter.description}
            </ProjectDescription>
          )}
        </ProjectInfo>
      </ProjectCard>
    );
  };

  return (
    <Layout
      title={t('projects.title', 'Projects')}
      description={t('projects.subtitle', 'Research Projects & Visualizations')}
      path="/projects"
    >
      <ProjectsHero>
        <HeroContent>
          <Title>{t('projects.title', 'Projects')}</Title>
          <Subtitle>
            {t('projects.subtitle', 'Research Projects & Visualizations')}
          </Subtitle>
        </HeroContent>
      </ProjectsHero>

      <ProjectsSection>
        {projects.length > 0 ? (
          <>
            {/* Work Projects */}
            {workProjects.length > 0 && (
              <CategorySection>
                <CategoryTitle>
                  {t('projects.category.work', 'Research')}
                </CategoryTitle>
                <ProjectGrid>
                  {workProjects.map(renderProjectCard)}
                </ProjectGrid>
              </CategorySection>
            )}

            {/* Fun Projects */}
            {funProjects.length > 0 && (
              <CategorySection>
                <CategoryTitle>
                  {t('projects.category.fun', 'Explorations')}
                </CategoryTitle>
                <ProjectGrid>
                  {funProjects.map(renderProjectCard)}
                </ProjectGrid>
              </CategorySection>
            )}
          </>
        ) : (
          <EmptyState>
            <h2>{t('projects.empty', 'No projects found')}</h2>
            <p>{t('projects.emptyDescription', 'Check back soon for new research projects.')}</p>
          </EmptyState>
        )}
      </ProjectsSection>
    </Layout>
  );
}

export default ProjectsPage;

export const query = graphql`
  query ProjectsPageQuery($language: String!) {
    allMarkdownRemark(
      filter: {
        frontmatter: { type: { eq: "projects" }, lang: { eq: $language } }
      }
      sort: { frontmatter: { importance: ASC } }
    ) {
      nodes {
        id
        frontmatter {
          page_id
          title
          description
          img
          category
          importance
          lang
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
